import { Fragment, useMemo } from 'react';
import { BigText, Content, Stack, Strong, Text } from '@kinesis/bungle';
import { DisclosureSourcePage } from '@/types';
import { renderActivity, renderMeasureClass } from '@/data/labels';
import {
  render,
  renderShare,
  renderUnit,
  renderValueOptional,
} from '@/data/units';
import { sourceColors, tint } from '@/data/colors';
import { ShareBar } from '@/components/share-bar';
import { DisclosureTreemap } from '@/components/disclosure-treemap';
import {
  DisclosureTable,
  Cell,
} from '@/components/disclosure-table/disclosure-table.styles';

// Renders a per-source-type disclosure page: the source's headline total + its
// share of the grand total, the measure-class breakdown table (one row per
// class with its activity in its own unit, CO₂-e, and share of the source as
// % + a coloured bar, ranked largest-first), and the "summary by activity"
// treemap (Activity → MeasureClass, sized by CO₂-e).
//
// Two server-resolved unit scopes (the FE never picks a unit):
//   - CO₂-e shares one unit across the headline + the value column.
//   - Activity differs per measure-class (kWh, MJ, t, …) — each row carries
//     its own unit inline via `render`; that column has no total.
// Both `share`s are server-computed: the page `share` is of the GRAND total
// (the "% of total emissions" line); each row `share` is of the SOURCE total.
// The whole page is the source's colour family — the table bars are the
// source colour; the treemap tints each activity from it. "Measure" here means
// measure-class. Store-less per the component/page split; the page owns the RTK call.
const DisclosureSource = (props: DisclosureSourceProps) => {
  const { pageData } = props;
  const { sourceType, unit, total, share, measureClasses, byActivity } =
    pageData;
  const color = sourceColors[sourceType];
  // Map the payload into the treemap's shape. Each activity gets a distinct
  // tint of the source colour so they read apart; the treemap shades the
  // measure-class leaves further. Label + palette lookup only — the totals
  // come straight from the server. Memoised so the treemap's `options` (and
  // thus the chart) only rebuild when `byActivity` actually changes, not on
  // every incidental page re-render.
  const treemapGroups = useMemo(() => {
    const groupCount = byActivity.length;
    return byActivity.map((activity, i) => ({
      key: activity.activity,
      label: renderActivity(activity.activity),
      color: tint(color, groupCount > 1 ? (i / (groupCount - 1)) * 0.5 : 0),
      children: activity.measureClasses.map((measureClass) => ({
        key: measureClass.measureClass,
        label: renderMeasureClass(measureClass.measureClass),
        value: measureClass.total,
      })),
    }));
  }, [byActivity, color]);
  return (
    <Stack space='medium'>
      <Stack space='small'>
        <Text tone='secondary'>Total emissions</Text>
        {/* `total` is absent for an entirely activity-only source (every
            measure-class has activity but no emissions factor) — render
            blank, never a fabricated 0. */}
        <BigText>{renderValueOptional(total, unit)}</BigText>
        <Text tone='secondary'>
          {renderUnit(unit)} · {renderShare(share)} of total emissions
        </Text>
      </Stack>
      {measureClasses.length > 0 && (
        <Stack space='small'>
          <Strong>Emissions by measure</Strong>
          <Content
            background='white'
            padding='medium'
            border
            paddingMode='equal'
            borderRadiusSize='small'
          >
            <DisclosureTable $columns='minmax(0, 1fr) minmax(96px, 0.4fr) auto auto auto'>
              <Cell $heading>Measure</Cell>
              <Cell $heading aria-hidden='true' />
              <Cell $heading $align='right'>
                Activity
              </Cell>
              <Cell $heading $align='right'>
                {renderUnit(unit)}
              </Cell>
              <Cell $heading $align='right'>
                %
              </Cell>
              {measureClasses.map((row) => (
                <Fragment key={row.measureClass}>
                  <Cell>{renderMeasureClass(row.measureClass)}</Cell>
                  <Cell>
                    <ShareBar share={row.share} color={color} />
                  </Cell>
                  <Cell $align='right'>
                    {render(row.activity, row.activityUnit)}
                  </Cell>
                  {/* Absent CO₂-e (activity-only class) renders blank, not 0. */}
                  <Cell $align='right'>
                    {renderValueOptional(row.total, unit)}
                  </Cell>
                  <Cell $align='right'>{renderShare(row.share)}</Cell>
                </Fragment>
              ))}
              <Cell $heading>Total</Cell>
              <Cell $heading aria-hidden='true' />
              <Cell $heading aria-hidden='true' />
              <Cell $heading $align='right'>
                {renderValueOptional(total, unit)}
              </Cell>
              <Cell $heading $align='right'>
                {renderShare(1)}
              </Cell>
            </DisclosureTable>
          </Content>
        </Stack>
      )}
      {byActivity.length > 0 && (
        <Stack space='small'>
          <Strong>Summary by activity</Strong>
          <Content
            background='white'
            padding='medium'
            border
            paddingMode='equal'
            borderRadiusSize='small'
          >
            <DisclosureTreemap groups={treemapGroups} unit={unit} />
          </Content>
        </Stack>
      )}
    </Stack>
  );
};

export type DisclosureSourceProps = {
  pageData: DisclosureSourcePage;
};

export { DisclosureSource };
